import { getProfileData } from './api';

const CACHE_TTL_MS        = 6 * 60 * 60 * 1000; // 6 hours
const CONCURRENCY_LIMIT   = 4;
const DEFAULT_COLOR       = '#5A2929';

// Cache values are either a hex string ('#rrggbb') or a full image URL ('https://...')
const memCache   = {};   // key → hex string | image URL
const inflight   = {};   // key → Promise<string>

let activeCount = 0;
const waitQueue = [];

function withSemaphore(fn) {
    return new Promise((resolve, reject) => {
        const run = () => {
            activeCount++;
            Promise.resolve()
                .then(fn)
                .then(resolve, reject)
                .finally(() => {
                    activeCount--;
                    const next = waitQueue.shift();
                    if (next) next();
                });
        };
        if (activeCount < CONCURRENCY_LIMIT) run();
        else waitQueue.push(run);
    });
}

const COSMETIC_API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// Uploaded cosmetics live on the API; build-shipped ones sit under /img/.
// The stored path says which, so nothing needs an "uploaded" flag.
export const resolveCosmeticUrl = (value) => {
    const s = String(value ?? '').trim();
    if (s.startsWith('/circlebgassets/') || s.startsWith('/skinassets/')) return `${COSMETIC_API_URL}${s}`;
    return s;
};

const safeHex = (v) =>
    /^#[0-9A-Fa-f]{6}$/.test(String(v ?? '').trim()) ? String(v).trim() : '';

// Returns the value if it's a valid image URL or relative image path, otherwise ''.
const safeImageUrl = (v) => {
    const s = String(v ?? '').trim();
    // Absolute URL
    try {
        const u = new URL(s);
        if (u.protocol === 'https:' || u.protocol === 'http:') return s;
    } catch { /* not an absolute URL */ }
    // Relative path (e.g. /img/frankCircleBgs/STR.png)
    if (/^\/img\/[^\s]+\.(?:png|jpg|jpeg|gif|webp|svg)$/i.test(s)) return s;
    // Uploaded cosmetic, served from the API
    if (/^\/(?:circlebgassets|skinassets)\/[^\s]+\.(?:png|jpg|jpeg|gif|webp|svg)$/i.test(s)) {
        return resolveCosmeticUrl(s);
    }
    return '';
};

/**
 * Resolves an avatarBg value from customisation JSON.
 * Priority: valid hex → valid https URL → pageBg hex → DEFAULT_COLOR
 */
const resolveAvatarBg = (payload) => {
    if (!payload) return DEFAULT_COLOR;
    const hex = safeHex(payload?.avatarBg);
    if (hex) return hex;
    const img = safeImageUrl(payload?.avatarBg);
    if (img) return img;
    return safeHex(payload?.pageBg) || DEFAULT_COLOR;
};

/**
 * Returns true if the resolved value is an image URL (not a hex colour).
 * Useful for callers that need to decide between backgroundColor and backgroundImage.
 */
export const isAvatarBgImage = (value) => !!safeImageUrl(value);

/**
 * Returns the correct React inline style object for an avatar circle background.
 * Pass the value returned by fetchProfileColor (hex string or image URL).
 * Optionally merge extra style properties via the second argument.
 *
 * Usage:
 *   style={getAvatarBgStyle(avatarBg)}
 *   style={getAvatarBgStyle(avatarBg, { width: 40, height: 40 })}
 */
export const getAvatarBgStyle = (value, extra = {}) => {
    const s = String(value ?? '').trim();
    if (safeImageUrl(s)) {
        return {
            backgroundImage: `url(${s})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            ...extra,
        };
    }
    return {
        backgroundColor: safeHex(s) || DEFAULT_COLOR,
        ...extra,
    };
};

/**
 * Returns a safe hex colour for use in colour-math operations (e.g. mixWithWhite).
 * If the value is an image URL, returns the DEFAULT_COLOR fallback instead.
 */
export const safeHexOrFallback = (value, fallback = DEFAULT_COLOR) => {
    const s = String(value ?? '').trim();
    return safeHex(s) || fallback;
};

const cacheKey = (name) =>
    `profileBg:${String(name || '').trim().toLowerCase()}`;

function readSession(name) {
    try {
        const raw = sessionStorage.getItem(cacheKey(name));
        if (!raw) return null;
        const { color, ts } = JSON.parse(raw);
        if (!color || !Number.isFinite(Number(ts))) return null;
        if (Date.now() - Number(ts) > CACHE_TTL_MS) return null;
        // Accept both hex and image URLs stored in session
        const value = String(color).trim();
        if (!safeHex(value) && !safeImageUrl(value)) return null;
        return value;
    } catch {
        return null;
    }
}

function writeSession(name, color) {
    try {
        sessionStorage.setItem(cacheKey(name), JSON.stringify({ color, ts: Date.now() }));
    } catch { /* quota exceeded or SSR — safe to swallow */ }
}

export async function fetchProfileColor(habboName) {
    const name = String(habboName || '').trim();
    if (!name) return DEFAULT_COLOR;
    const key = name.toLowerCase();

    // 1. in-memory hit
    if (memCache[key]) return memCache[key];

    // 2. sessionStorage hit
    const sessionHit = readSession(name);
    if (sessionHit) {
        memCache[key] = sessionHit;
        return sessionHit;
    }

    // 3. deduplicate concurrent callers
    if (inflight[key]) return inflight[key];

    inflight[key] = withSemaphore(async () => {
        let color = DEFAULT_COLOR;
        try {
            const r = await getProfileData(name, 'customisation');
            let payload = r?.data?.jsonPayload;
            if (typeof payload === 'string') {
                try { payload = JSON.parse(payload); } catch { payload = {}; }
            }
            if (r?.success && payload) {
                color = resolveAvatarBg(payload);
            }
        } catch { /* network error — fall through to default */ }

        memCache[key] = color;
        writeSession(name, color);
        return color;
    });

    try {
        return await inflight[key];
    } finally {
        delete inflight[key];
    }
}

export function invalidateProfileColor(habboName) {
    const key = String(habboName || '').trim().toLowerCase();
    delete memCache[key];
    try { sessionStorage.removeItem(cacheKey(habboName)); } catch {}
}